/**
 * Notification Context
 * Manages in-app notifications and real-time alerts
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as socketService from '../services/socket';
import { notificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { subscribeToPush, unsubscribeFromPush, isPushSupported } from '../services/pushNotifications';

const NotificationContext = createContext(null);

/**
 * Update the app badge count via service worker
 * Works on iOS Safari, Chrome (desktop/Android 81+), and Edge
 */
async function updateAppBadge(count) {
  try {
    // Direct Badging API (works on some browsers without service worker)
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    }

    // Also notify service worker to keep badge in sync
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: count > 0 ? 'SET_BADGE_COUNT' : 'CLEAR_BADGE',
        count: count
      });
    }
  } catch (err) {
    // Badge API not supported or failed - silent fail
    console.log('Badge update failed:', err.message);
  }
}

export function NotificationProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pushSubscribedRef = useRef(false);

  // Fetch persisted notifications on mount
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const response = await notificationsAPI.getAll(50);
      if (response.success && response.data) {
        setNotifications(response.data.map(n => ({
          ...n,
          read: !!n.read_at,
          timestamp: n.created_at
        })));
        const newUnreadCount = response.unread_count || 0;
        setUnreadCount(newUnreadCount);
        // Sync badge with actual unread count
        updateAppBadge(newUnreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch notifications when authenticated
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe to push notifications when user logs in
  useEffect(() => {
    if (user && isAuthenticated && !pushSubscribedRef.current) {
      pushSubscribedRef.current = true;
      if (isPushSupported()) {
        subscribeToPush().catch(err =>
          console.log('Push subscription skipped:', err.message || err)
        );
      }
    }
    if (!isAuthenticated) {
      pushSubscribedRef.current = false;
    }
  }, [user, isAuthenticated]);

  // Handle notification clicks from service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK' && event.data.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Listen for real-time notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    // Main notification listener - this now adds to the existing list
    const unsubNotification = socketService.on(socketService.EVENTS.NOTIFICATION, (notification) => {
      try {
        // Validate notification data to prevent crashes
        if (!notification || typeof notification !== 'object') {
          console.error('Invalid notification data received:', notification);
          return;
        }

        // Add the real-time notification to the top of the list
        const newNotification = {
          id: notification.id || Date.now(),
          timestamp: notification.created_at || new Date().toISOString(),
          read: false,
          ...notification,
        };
        setNotifications(prev => [newNotification, ...prev.filter(n => n.id !== notification.id)].slice(0, 50));
        setUnreadCount(prev => {
          const newCount = prev + 1;
          updateAppBadge(newCount);
          return newCount;
        });
      } catch (error) {
        console.error('Error processing notification:', error);
      }
    });

    // New request listener (for helpers - new jobs in their area)
    const unsubNewRequest = socketService.on(socketService.EVENTS.NEW_REQUEST, (data) => {
      // Don't duplicate - these are now persisted and sent via 'notification' event
    });

    // New application listener (for owners)
    const unsubNewApp = socketService.on(socketService.EVENTS.NEW_APPLICATION, (data) => {
      // Don't duplicate - these are now persisted and sent via 'notification' event
    });

    // Helper selected listener (for helpers)
    const unsubSelected = socketService.on('selected', (data) => {
      // Don't duplicate - these are now persisted and sent via 'notification' event
    });

    return () => {
      unsubNotification();
      unsubNewRequest();
      unsubNewApp();
      unsubSelected();
    };
  }, [isAuthenticated]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: notification.id || Date.now(),
      timestamp: notification.created_at || new Date().toISOString(),
      read: false,
      ...notification,
    };

    setNotifications(prev => [newNotification, ...prev.filter(n => n.id !== notification.id)].slice(0, 50));
    setUnreadCount(prev => {
      const newCount = prev + 1;
      updateAppBadge(newCount);
      return newCount;
    });
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => {
      const newCount = Math.max(0, prev - 1);
      updateAppBadge(newCount);
      return newCount;
    });

    // Persist to server
    try {
      await notificationsAPI.markAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    updateAppBadge(0);

    // Persist to server
    try {
      await notificationsAPI.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    // Save previous state for rollback
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;

    // Optimistic update
    const notification = notifications.find(n => n.id === notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notification && !notification.read) {
      const newCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newCount);
      updateAppBadge(newCount);
    }

    // Persist to server
    try {
      await notificationsAPI.delete(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Rollback on failure
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
      updateAppBadge(prevUnreadCount);
    }
  }, [notifications, unreadCount]);

  const handleLogoutPush = useCallback(async () => {
    try {
      await unsubscribeFromPush();
    } catch (err) {
      // Silent - don't block logout
    }
  }, []);

  const clearNotifications = useCallback(async () => {
    // Save previous state for rollback
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;

    // Optimistic update
    setNotifications([]);
    setUnreadCount(0);
    updateAppBadge(0);

    // Persist to server
    try {
      await notificationsAPI.clearAll();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      // Rollback on failure
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
      updateAppBadge(prevUnreadCount);
    }
  }, [notifications, unreadCount]);

  // Toast notifications (temporary alerts)
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const toast = { id, message, type };

    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const dismissToast = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    toasts,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearNotifications,
    showToast,
    dismissToast,
    fetchNotifications,
    handleLogoutPush,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
